import React, { useState } from "react";
import { Row, Col, Table, Badge, ProgressBar, Button } from "react-bootstrap";
import Plot from "react-plotly.js";

const SystemsConnectionByCountry = ({ dataCountry, data }) => {

  const [mode, setMode] = useState("count");

  if (!data || !dataCountry) return null;

  const countryAliases = {
    "bosnia and herzegovina": "bosnia",
    "korea": "south korea"
  };

  const devices = JSON.parse(data);
  const countries = JSON.parse(dataCountry);

  const devicesArray = Array.isArray(devices) ? devices : Object.values(devices);
  const countryArray = Array.isArray(countries) ? countries : Object.values(countries);

  const laserNames = ["LaserGenius +", "Platino Linear"];

  const laserDevices = devicesArray.filter(d => laserNames.includes(d.device));
  const automationDevices = devicesArray.filter(d => !laserNames.includes(d.device));

  const regionOrder = ["RU_SEMEA","RU_NEU","RU_AME","RU_APAC","Unknown"];

  const colors = {
    active: "#2ca02c",
    inactive: "#ff7f0e",
    never: "#d62728"
  };


  const stateLabels = {
    active: "Active",
    inactive: "Inactive",
    never: "Never Active"
  };

  // COUNTRY → REGION

  const countryRegionMap = {};

  countryArray.forEach(c => {
    countryRegionMap[c.Country?.toLowerCase()] = c["Sales Region"];
  });

  // AGGREGATION

  const aggregate = (dataset) => {

    const map = {};

    dataset.forEach(d => {

      let country = (d.country || "").trim().toLowerCase();

      if (countryAliases[country]) {
        country = countryAliases[country];
      }

      const region = countryRegionMap[country] || "Unknown";

      if (!map[region]) {
        map[region] = { active:0, inactive:0, never:0 };
      }

      if (d.stato === "active") map[region].active++;
      if (d.stato === "inactive") map[region].inactive++;
      if (d.stato === "never active") map[region].never++;

    });

    return map;
  };

  const allAgg = aggregate(devicesArray);
  const laserAgg = aggregate(laserDevices);
  const autoAgg = aggregate(automationDevices);

  const datasets = [
    { name:"All", agg: allAgg },
    { name:"Laser", agg: laserAgg },
    { name:"Automation", agg: autoAgg }
  ];

  const states = ["active","inactive","never"];

  const regions = regionOrder.filter(r =>
    allAgg[r] || laserAgg[r] || autoAgg[r]
  );

  // PERCENT

  const percent = (v,total) => {
    if (!total) return 0;
    return ((v/total)*100).toFixed(1);
  };

  // --------- PLOT ---------

  const traces = [];

  const cols = 2;
  const rows = Math.ceil(regions.length / cols);

  regions.forEach((region,i)=>{

    states.forEach(state=>{

      datasets.forEach(ds=>{

        const total =
          (ds.agg[region]?.active || 0) +
          (ds.agg[region]?.inactive || 0) +
          (ds.agg[region]?.never || 0);

        const value = ds.agg[region]?.[state] || 0;

        const yValue =
          mode === "percent" && total
            ? (value/total)*100
            : value;

        traces.push({

          x:[ds.name],
          y:[yValue],

          type:"bar",

          marker:{color:colors[state]},

          name:stateLabels[state],
          legendgroup:state,

          showlegend:i===0 && ds.name==="All",

          xaxis:"x"+(i+1),
          yaxis:"y"+(i+1)

        });

      });

    });

  });

  const layout = {

    grid:{
      rows:rows,
      columns:cols,
      pattern:"independent"
    },

    barmode:"stack",

    height:rows*350,

    legend:{
      orientation:"h",
      x:0.5,
      xanchor:"center",
      y:-0.05
    },

    margin:{t:80,l:60,r:20,b:40},

    annotations:regions.map((r,i)=>({
      text:`<b>${r}</b>`,
      xref:"x"+(i+1)+" domain",
      yref:"y"+(i+1)+" domain",
      x:0.5,
      y:1.15,
      showarrow:false,
      font: { 
        size: 18,
        family: "Comfortaa, sans serif",
        }
    }))
  };

  return (

  <>

  {/* TITLE */}

  <Row className="mt-1 mb-4">
    <Col md={{ span: 8, offset: 2 }}>
      <h4 className="text-center">
        Analisi per Region Unit
      </h4>
    </Col>
  </Row>

  {/* MODE BUTTONS */}

  <Row className="mb-3">
    <Col className="text-center">

      <Button
        variant={mode==="count"?"primary":"outline-primary"}
        onClick={()=>setMode("count")}
        className="me-2"
      >
        Count
      </Button>

      <Button
        variant={mode==="percent"?"primary":"outline-primary"}
        onClick={()=>setMode("percent")}
      >
        %
      </Button>

    </Col>
  </Row>

  {/* CHART */}

  <Row>
    <Col>
      <Plot
        data={traces}
        layout={layout}
        style={{ width: "100%" }}
        useResizeHandler={true}
        config={{ responsive: true }}
      />
    </Col>
  </Row>

  {/* TABLE DASHBOARD */}

  <Row className="mt-4">
    <Col md={{ span:10, offset:1 }}>

      <Table striped bordered hover>

        <thead>
          <tr className="text-center">
            <th>Region</th>
            <th>Active</th>
            <th>Inactive</th>
            <th>Never Active</th>
            <th>Distribution</th>
          </tr>
        </thead>

        <tbody>

        {regions.map(region=>{

          const active = allAgg[region]?.active || 0;
          const inactive = allAgg[region]?.inactive || 0;
          const never = allAgg[region]?.never || 0;

          const total = active+inactive+never;

          const pActive = percent(active,total);
          const pInactive = percent(inactive,total);
          const pNever = percent(never,total);

          return(

          <tr key={region} className="text-center align-middle">

            <td>
              <b>{region}</b>
              <div style={{fontSize:12,color:"#777"}}>
                {total} devices
              </div>
            </td>

            <td>
              <Badge bg="success">
                {active} ({pActive}%)
              </Badge>
            </td>

            <td>
              <Badge bg="warning" text="dark">
                {inactive} ({pInactive}%)
              </Badge>
            </td>

            <td>
              <Badge bg="danger">
                {never} ({pNever}%)
              </Badge>
            </td>

            <td style={{minWidth:260}}>

              <ProgressBar>

                <ProgressBar
                  variant="success"
                  now={pActive}
                  key={1}
                />

                <ProgressBar
                  variant="warning"
                  now={pInactive}
                  key={2}
                />

                <ProgressBar
                  variant="danger"
                  now={pNever}
                  key={3}
                />

              </ProgressBar>

            </td>

          </tr>

          )

        })}

        </tbody>

      </Table>

    </Col>
  </Row>

  </>

  );

};

export default SystemsConnectionByCountry;